import PersonIcon from '@mui/icons-material/Person';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Add } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Button,
    Grid,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import CustomTooltip from '../../../shared/CustomTooltip';
import UserRole from '../../../../shared/AuthRoles';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormSelect from '../../../shared/FormSelect';
import FormTextField from '../../../shared/FormTextField';
import ProfileSectionCard, {
    profileMobileInlineEditBottomReserve,
} from '../../Profile/ProfileSectionCard';
import ProfileReadOnlyField, {
    personalAccessTooltipContent,
    ProfileShellFormCell,
    ProfileStatusIndicator,
} from '../../Profile/ProfileReadOnlyField';
import {
    AdminRole,
    ContractorRole,
    GuestRole,
    OwnerRole,
} from '../../../enums/users';
import { capitalizeName } from '../../../../shared/utils';
import ManagedUserLoginSecurityPanel, {
    type ManagedUserInvite,
} from '../ManagedUserLoginSecurityPanel';

type RoleOption = { value: string; label: string; isDefault?: boolean };

function accessLabelFromRole(role: string | undefined, viewOnly: boolean) {
    if (!role) return '—';
    if (
        [AdminRole.Admin, OwnerRole.Admin, ContractorRole.Admin].includes(
            role as AdminRole,
        )
    )
        return 'Admin';
    if (role === 'guest_admin') return 'Company Admin';
    if (role === 'guest_department_admin') return 'Team Admin';
    if (viewOnly) return 'View Only User';
    return 'User';
}

export default function EmbeddedManagedUserEditShell({
    scope,
    onCancel,
    user,
    roleOptions,
    teamList,
    invite,
    canSendInvite,
    onSendOrResendInvite,
    loggedInRole,
    viewOnly,
}: {
    scope: 'personal' | 'login';
    onCancel: () => void;
    user: {
        lastLoginAt?: string | Date | null;
        isActive?: boolean;
    };
    roleOptions: RoleOption[];
    teamList: { value: number; label: string }[];
    invite?: ManagedUserInvite | null;
    canSendInvite: boolean;
    onSendOrResendInvite: () => void;
    loggedInRole?: string;
    viewOnly?: boolean;
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const { control, formState } = useFormContext();
    const { isDirty, isSubmitting } = formState;
    const values = useWatch({ control }) as {
        firstName?: string;
        lastName?: string;
        email?: string;
        role?: string;
        department?: { id?: number | null } | null;
        phone?: string;
        vehiclePlate?: string;
        isActive?: boolean;
    };

    const teamLabelFromForm = (() => {
        const id = values.department?.id;
        if (id == null) return '—';
        const row = teamList.find((t) => t.value === id);
        return row?.label ? capitalizeName(row.label) : '—';
    })();

    const accessTooltip = (
        <CustomTooltip
            title={personalAccessTooltipContent(loggedInUser)}
            placement='right'
        >
            <HelpOutlineIcon sx={{ color: 'text.primary', fontSize: '16px' }} />
        </CustomTooltip>
    );

    const statusTooltip = (
        <CustomTooltip
            title={
                <Typography variant='body2'>
                    Enabled: user can sign in. Disabled: user cannot access the
                    site.
                </Typography>
            }
            placement='right'
        >
            <HelpOutlineIcon sx={{ color: 'text.primary', fontSize: '16px' }} />
        </CustomTooltip>
    );

    const teamTooltip = (
        <CustomTooltip
            title={
                <Typography variant='body2'>
                    By selecting the user&apos;s team, they appear under Team
                    Managers. Only a company admin can change a user&apos;s
                    team.
                </Typography>
            }
            placement='right'
        >
            <HelpOutlineIcon sx={{ color: 'text.primary', fontSize: '16px' }} />
        </CustomTooltip>
    );

    const vehicleTooltip = (
        <CustomTooltip
            title={
                <Typography variant='body2'>
                    Some experiences may require a plate number for site access.
                </Typography>
            }
            placement='right'
        >
            <HelpOutlineIcon sx={{ color: 'text.primary', fontSize: '16px' }} />
        </CustomTooltip>
    );

    const onSaveInline = () => {
        const el = document.getElementById(
            'managed-user-edit-form',
        ) as HTMLFormElement | null;
        el?.requestSubmit();
    };

    const personalGrid = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='First name'
                    fieldId='managed-user-first-name'
                >
                    <FormTextField
                        id='managed-user-first-name'
                        name='firstName'
                        control={control}
                        placeholder='John'
                        size='small'
                        fullWidth
                    />
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Surname'
                    fieldId='managed-user-surname'
                >
                    <FormTextField
                        id='managed-user-surname'
                        name='lastName'
                        control={control}
                        placeholder='Smith'
                        size='small'
                        fullWidth
                    />
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Phone'
                    fieldId='managed-user-phone'
                >
                    <FormTextField
                        id='managed-user-phone'
                        name='phone'
                        control={control}
                        placeholder='0414789456'
                        type='tel'
                        size='small'
                        fullWidth
                    />
                </ProfileShellFormCell>
            </Grid>
            {loggedInUser?.role.includes('guest') && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileShellFormCell
                        label='Team'
                        labelAdornment={teamTooltip}
                        fieldId='managed-user-team'
                    >
                        <FormAutocomplete
                            id='managed-user-team'
                            name='department.id'
                            control={control}
                            options={teamList}
                            disabled={
                                loggedInUser?.role === GuestRole.DepartmentAdmin
                            }
                            disableClearable={undefined}
                            PaperComponent={({ children }) => (
                                <Paper>
                                    <Button
                                        fullWidth
                                        size='large'
                                        sx={{
                                            justifyContent: 'flex-start',
                                            pl: 2,
                                            mt: 1,
                                        }}
                                        startIcon={<Add />}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            window.open('/newTeam', '_blank');
                                        }}
                                    >
                                        Create New Team
                                    </Button>
                                    {children}
                                </Paper>
                            )}
                        />
                    </ProfileShellFormCell>
                </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Access'
                    labelAdornment={accessTooltip}
                    fieldId='managed-user-access'
                >
                    <FormSelect
                        id='managed-user-access'
                        labelId='managed-user-access-label'
                        name='role'
                        control={control}
                        options={roleOptions}
                    />
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Status'
                    labelAdornment={statusTooltip}
                    fieldId='managed-user-status'
                >
                    <FormSelect
                        id='managed-user-status'
                        labelId='managed-user-status-label'
                        name='isActive'
                        control={control}
                        options={[
                            { value: true, label: 'Enabled' },
                            { value: false, label: 'Disabled' },
                        ]}
                    />
                </ProfileShellFormCell>
            </Grid>
            {loggedInUser?.role !== AdminRole.Admin && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileShellFormCell
                        label='Vehicle plate'
                        labelAdornment={vehicleTooltip}
                        fieldId='managed-user-vehicle-plate'
                    >
                        <FormTextField
                            id='managed-user-vehicle-plate'
                            name='vehiclePlate'
                            control={control}
                            size='small'
                            fullWidth
                        />
                    </ProfileShellFormCell>
                </Grid>
            )}
        </Grid>
    );

    const personalReadOnlyGrid = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='First name'>
                    {values.firstName}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Surname'>
                    {values.lastName}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Phone'>
                    {values.phone}
                </ProfileReadOnlyField>
            </Grid>
            {loggedInRole?.includes('guest') ? (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileReadOnlyField label='Team'>
                        {teamLabelFromForm}
                    </ProfileReadOnlyField>
                </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Access'>
                    {accessLabelFromRole(values.role, Boolean(viewOnly))}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Status'>
                    <ProfileStatusIndicator active={values.isActive} />
                </ProfileReadOnlyField>
            </Grid>
            {loggedInRole !== AdminRole.Admin ? (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileReadOnlyField label='Vehicle plate'>
                        {values.vehiclePlate}
                    </ProfileReadOnlyField>
                </Grid>
            ) : null}
        </Grid>
    );

    const loginBody = (
        <ManagedUserLoginSecurityPanel
            email={values.email}
            emailEditSlot={
                <FormTextField
                    id='managed-user-email'
                    name='email'
                    control={control}
                    size='small'
                    fullWidth
                />
            }
            lastLoginAt={user?.lastLoginAt}
            invite={invite}
            canSendInvite={canSendInvite}
            onSendOrResendInvite={onSendOrResendInvite}
            sendInviteDisabled={user?.isActive === false}
        />
    );

    const loginReadOnlyBody = (
        <ManagedUserLoginSecurityPanel
            email={values.email}
            lastLoginAt={user?.lastLoginAt}
            invite={invite}
            canSendInvite={canSendInvite}
            onSendOrResendInvite={onSendOrResendInvite}
            sendInviteDisabled={values.isActive === false}
        />
    );

    return (
        <Stack
            spacing={3}
            sx={{
                width: '100%',
                ...(isMobile
                    ? { pb: profileMobileInlineEditBottomReserve(theme) }
                    : {}),
            }}
        >
            {scope === 'personal' ? (
                <>
                    <ProfileSectionCard
                        icon={<PersonIcon sx={{ fontSize: 22 }} />}
                        title='Personal details'
                        subtitle='Personal information, role, and account access.'
                        editButtonLabel='Edit personal details'
                        onEdit={() => {}}
                        showEditButton={false}
                        inlineEditActive
                        onCancelInline={onCancel}
                        onSaveInline={onSaveInline}
                        saveDisabled={!isDirty || isSubmitting}
                    >
                        {personalGrid}
                    </ProfileSectionCard>
                    <ProfileSectionCard
                        icon={<ShieldOutlinedIcon sx={{ fontSize: 12 }} />}
                        title='Login & security'
                        subtitle='Login email, invitations, and access to the account.'
                        editButtonLabel='Edit login & security'
                        onEdit={() => {}}
                        showEditButton={false}
                    >
                        {loginReadOnlyBody}
                    </ProfileSectionCard>
                </>
            ) : (
                <>
                    <ProfileSectionCard
                        icon={<PersonIcon sx={{ fontSize: 22 }} />}
                        title='Personal details'
                        subtitle='Personal information, role, and account access.'
                        editButtonLabel='Edit personal details'
                        onEdit={() => {}}
                        showEditButton={false}
                    >
                        {personalReadOnlyGrid}
                    </ProfileSectionCard>
                    <ProfileSectionCard
                        icon={<ShieldOutlinedIcon sx={{ fontSize: 12 }} />}
                        title='Login & security'
                        subtitle='Login email, invitations, and access to the account.'
                        editButtonLabel='Edit login & security'
                        onEdit={() => {}}
                        showEditButton={false}
                        inlineEditActive
                        onCancelInline={onCancel}
                        onSaveInline={onSaveInline}
                        saveDisabled={!isDirty || isSubmitting}
                    >
                        {loginBody}
                    </ProfileSectionCard>
                </>
            )}
        </Stack>
    );
}
